// AddFanDialog.tsx
import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Divider,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Button,
  Text,
  Box,
  Flex,
  useToast,
} from "@chakra-ui/react";
import { useForm, Controller, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import KYDInput from "src/ui/KYDInput";
import actions from "src/actions";

import { KYDLabeledDatePicker, KYDLabeledSelect, KYDMultiSelect } from "src/ui";
import dayjs from "dayjs";
import { KYDGender } from "common/interfaces/KYDUser";
import { KYDContact, KYDFullContact } from "@common/interfaces/KYDCampaign";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import parsePhoneNumber from "libphonenumber-js";

type TagOption = { label: string; value: string };

export interface AddFanFormValues {
  first_name?: string;
  last_name?: string;
  email: string;
  phone?: string;
  dob?: string;
  tags: TagOption[] | string[];
  gender?: KYDGender | undefined;
}

const schema = yup.object({
  first_name: yup.string().optional().nullable().notRequired(),
  last_name: yup.string().optional().nullable().notRequired(),
  email: yup.string().email("Invalid email").required("Email is required"),
  phone: yup
    .string()
    .transform((val, orig) => (orig === "" ? null : val))
    .nullable()
    .notRequired(),
  dob: yup
    .string()
    .nullable()
    .notRequired()
    .matches(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, {
      message: "Format: YYYY-MM-DD",
      excludeEmptyString: true,
    }),
  gender: yup
    .string()
    .oneOf(["male", "female", "prefernottosay", ""], "Invalid gender")
    .transform((val, orig) => (orig === "" ? undefined : val))
    .optional()
    .nullable()
    .notRequired(),
  tags: yup
    .array()
    .of(
      yup.object({
        label: yup.string().required(),
        value: yup.string().required(),
      })
    )
    .nullable()
    .notRequired(),
});

const AddFanDialog: React.FC<{
  title?: string;
  isOpen: boolean;
  contact?: KYDFullContact | null;
  onCreate?: (contact: KYDContact) => void;
  onUpdate?: () => void;
  onClose: () => void;
}> = ({ title, contact, onCreate, onUpdate, onClose, isOpen }) => {
  const isEditMode = !!contact;
  const hasUserId = !!contact?.user_id;
  const defaultTitle = isEditMode ? "Edit contact info" : "Create new contact";
  const modalTitle = title || defaultTitle;

  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [tags, setTags] = useState<{ label: string; value: string }[]>([]);

  const fetchOrganizationTags = useCallback(async () => {
    const res = await actions.ADMIN.fetchOrganizationTags();
    setTags(res.map((tag) => ({ label: tag, value: tag })));
  }, []);

  useEffect(() => {
    fetchOrganizationTags();
  }, [fetchOrganizationTags]);

  const form = useForm<AddFanFormValues>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      dob: "",
      gender: undefined,
      tags: [],
    },
  });

  const {
    handleSubmit,
    control,
    register,
    watch,
    reset,
    formState: { isDirty, dirtyFields },
  } = form;

  // Reset form when contact changes or modal opens in edit mode
  useEffect(() => {
    if (contact && isOpen && isEditMode) {
      // Convert dob to YYYY-MM-DD format if it exists and is in a different format
      let dobFormatted = "";
      if (contact.dob) {
        // Try to parse the date and format it as YYYY-MM-DD
        const parsedDate = dayjs(contact.dob);
        if (parsedDate.isValid()) {
          dobFormatted = parsedDate.format("YYYY-MM-DD");
        } else {
          // If parsing fails, try to handle MM/DD/YYYY format
          const parts = contact.dob.split("/");
          if (parts.length === 3) {
            const month = parts[0].padStart(2, "0");
            const day = parts[1].padStart(2, "0");
            const year = parts[2];
            dobFormatted = `${year}-${month}-${day}`;
          } else {
            dobFormatted = contact.dob;
          }
        }
      }

      // Convert phone number for PhoneInput (needs to be in format: country code + number, e.g., "12035551234")
      let phoneFormatted = "";
      if (contact.phone_number) {
        const phoneStr = String(contact.phone_number);
        // Remove any non-digit characters
        const digitsOnly = phoneStr.replace(/\D/g, "");
        // If it starts with 1 (US country code), use as is, otherwise assume it's a US number and prepend 1
        if (digitsOnly.length === 10) {
          phoneFormatted = "1" + digitsOnly;
        } else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
          phoneFormatted = digitsOnly;
        } else if (digitsOnly.length > 0) {
          phoneFormatted = digitsOnly;
        }
      }

      reset({
        first_name: contact.first_name || "",
        last_name: contact.last_name || "",
        email: contact.email || "",
        phone: phoneFormatted,
        dob: dobFormatted,
        gender: contact.gender || undefined,
        tags: (contact.tags || []).map((tag) => ({
          label: tag,
          value: tag,
        })),
      });
    } else if (!contact && isOpen && !isEditMode) {
      reset({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        dob: "",
        gender: undefined,
        tags: [],
      });
    }
  }, [contact, isOpen, isEditMode, reset]);

  function isValidCheck(values: {
    first_name?: string;
    last_name?: string;
    email?: string;
  }): boolean {
    const { email = "" } = values;
    const emailRegex = /^\S+@\S+\.\S+$/;

    return emailRegex.test(email.trim());
  }

  const [first_name, last_name, email] = watch([
    "first_name",
    "last_name",
    "email",
  ]);

  const isValid = isValidCheck({ first_name, last_name, email });

  // For contacts with user_id in edit mode, only tags can be changed
  const isTagsDirty = hasUserId && isEditMode && dirtyFields.tags;
  // For create mode or edit mode without user_id, need all fields to be valid and dirty
  const canSave = hasUserId && isEditMode ? isTagsDirty : isDirty && isValid;

  const _onSubmit = async (data: AddFanFormValues) => {
    try {
      setSubmitting(true);

      if (isEditMode && contact) {
        // If contact has user_id, only update tags
        const updateData: Partial<KYDFullContact> = {
          id: contact.id,
          tags: data.tags.map((t) => (typeof t === "string" ? t : t.value)),
        };

        if (!hasUserId) {
          // Only include other fields if contact doesn't have user_id
          updateData.first_name = data.first_name;
          updateData.last_name = data.last_name;
          updateData.email = data.email;
          // PhoneInput returns format like "12035551234", use parsePhoneNumber to convert to E.164 format
          if (data.phone) {
            const phoneStr = String(data.phone);
            // PhoneInput returns country code + number without +, so prepend + for parsing
            const parsedPhoneNumber = parsePhoneNumber(`+${phoneStr}`, {
              defaultCountry: "US",
            });
            if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
              updateData.phone_number = parsedPhoneNumber.number; // E.164 format
            } else {
              // Invalid phone number, set to null
              updateData.phone_number = null;
            }
          } else {
            // Send null to explicitly clear the phone number
            updateData.phone_number = null;
          }
          updateData.dob = data.dob || undefined;
          updateData.gender = data.gender || undefined;
        }

        await actions.ADMIN.updateContact(updateData);
        toast({
          title: "Contact updated",
          description: "Contact updated successfully",
          status: "success",
        });
        onUpdate?.();
      } else {
        const res = await actions.ADMIN.createFan({
          ...data,
          tags: data.tags.map((t) => (typeof t === "string" ? t : t.value)),
        });
        onCreate?.(res);
        toast({
          title: "Contact created",
          description: "Contact created successfully",
          status: "success",
        });
        form.reset();
      }

      onClose();
    } catch (error: any) {
      toast({
        title: isEditMode ? "Error updating contact" : "Error creating contact",
        description:
          error.message ||
          (isEditMode
            ? "Failed to update contact"
            : "Failed to create contact"),
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent bg="white" borderRadius="lg" maxW="600px" w="100%" p={0}>
        <FormProvider {...form}>
          <Box p={6}>
            <ModalHeader p={0} mb={4} fontSize="2xl">
              <HStack justify="space-between">
                <Text fontWeight="400" fontSize="24px">
                  {modalTitle}
                </Text>
                <HStack spacing={3}>
                  <Button
                    variant="outline"
                    onClick={onClose}
                    borderRadius="md"
                    fontSize="13px"
                  >
                    Cancel
                  </Button>
                  <Button
                    colorScheme="green"
                    onClick={handleSubmit(_onSubmit)}
                    isDisabled={!canSave || submitting}
                    borderRadius="md"
                    fontSize="13px"
                    isLoading={submitting}
                  >
                    {isEditMode ? "Save" : "Create"}
                  </Button>
                </HStack>
              </HStack>
            </ModalHeader>

            <Divider mb={6} />

            <ModalBody p={0}>
              {hasUserId && isEditMode && (
                <Box pb={4}>
                  <Text fontSize="14px" fontWeight={500} color="#1A202C99">
                    User provided contact details are not editable. Tags can be
                    updated for filtering and campaign targeting.
                  </Text>
                </Box>
              )}
              <VStack spacing={4} align="stretch">
                <Controller
                  name="first_name"
                  control={control}
                  render={({ field }) => (
                    <KYDInput
                      label="First Name"
                      placeholder=""
                      {...field}
                      isDisabled={hasUserId}
                    />
                  )}
                />
                <Controller
                  name="last_name"
                  control={control}
                  render={({ field }) => (
                    <KYDInput
                      label="Last Name"
                      placeholder=""
                      {...field}
                      isDisabled={hasUserId}
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <KYDInput
                      label="Email"
                      placeholder=""
                      {...field}
                      isDisabled={hasUserId}
                    />
                  )}
                />

                {!hasUserId && (
                  <FormControl>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <PhoneInput
                          disabled={hasUserId}
                          autoFormat={true}
                          inputProps={{
                            placeholder: "Phone Number",
                          }}
                          containerStyle={{
                            width: "100%",
                          }}
                          dropdownStyle={{
                            zIndex: 9999,
                          }}
                          inputStyle={{
                            width: "100%",
                            height: "45px",
                            fontFamily: "Work Sans",
                            borderWidth: "1px",
                            borderRadius: "7px",
                            borderColor: "rgba(0,0,0,0.1)",
                            fontSize: "14px",
                          }}
                          buttonStyle={{
                            borderWidth: "1px",
                            borderRadius: "7px 0 0 7px",
                            borderColor: "rgba(0,0,0,0.1)",
                            borderRight: "none",
                            zIndex: 1,
                          }}
                          countryCodeEditable={false}
                          country={"us"}
                          onChange={onChange}
                          value={value || ""}
                        />
                      )}
                    />
                  </FormControl>
                )}
                <Controller
                  name="dob"
                  control={control}
                  render={({ field }) => (
                    <KYDLabeledDatePicker
                      name="dob"
                      label="Date of Birth"
                      value={field.value || ""}
                      onChange={(dayjs) => {
                        if (dayjs) {
                          field.onChange(dayjs.format("YYYY-MM-DD"));
                        } else {
                          field.onChange("");
                        }
                      }}
                      isDisabled={hasUserId}
                      dateTimeProps={{
                        defaultCalendarMonth: dayjs().subtract(18, "year"),
                        format: "YYYY-MM-DD",
                      }}
                    />
                  )}
                />

                <KYDLabeledSelect
                  name="gender"
                  label="Gender"
                  options={[
                    { label: "Not selected", value: "" },
                    { label: "Male", value: "male" },
                    { label: "Female", value: "female" },
                    { label: "Prefer not to say", value: "prefernottosay" },
                  ]}
                  placeholder=""
                  isDisabled={hasUserId}
                  {...register("gender")}
                />

                {/* Tag Multi-Select */}
                <FormControl>
                  <Controller
                    name="tags"
                    control={control}
                    render={({ field }) => (
                      <KYDMultiSelect
                        createable
                        values={field.value as TagOption[]}
                        onChange={(vals) => field.onChange(vals)}
                        options={tags}
                        placeholder="Tags"
                        minHeight="24px"
                      />
                    )}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
          </Box>

          <Divider />
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};

export default AddFanDialog;
